/**
 * Thumbnail Panel Component
 *
 * Displays thumbnails for all images in a DICOM study
 * Allows navigation to specific images and shows current selection
 */

import { useEffect, useRef, useState, useMemo } from 'react'
import { ImageIcon, Layers, ChevronDown, ChevronUp, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { conCredencial, type Portal } from '@/lib/media-url'

/**
 * Lo mínimo que el panel necesita de una imagen.
 *
 * Antes exigía el tipo completo del servicio del PERSONAL, que trae campos
 * que el portal del paciente no expone (rutas de almacenamiento, tamaños).
 * Pedir solo lo que se pinta deja que las dos fuentes encajen sin que ninguna
 * tenga que fingir campos que no tiene.
 */
export interface ImagenEnPanel {
  id: string
  seriesInstanceUID: string
  instanceNumber: number | null
  thumbnailUrl: string | null
}

export interface SerieEnPanel {
  seriesInstanceUID: string
  seriesNumber: number | null
  seriesDescription: string | null
  instanceCount: number
}

interface ThumbnailPanelProps {
  /** Study instances with URLs */
  instances: ImagenEnPanel[]
  /** Series information */
  series?: SerieEnPanel[]
  /** Currently selected instance index */
  currentIndex: number
  /** Callback when thumbnail is clicked */
  onSelect: (index: number) => void
  /** Loading state */
  isLoading?: boolean
  /** Panel position */
  position?: 'left' | 'right'
  /** Con qué credencial se piden las miniaturas */
  portal: Portal
}

export function ThumbnailPanel({
  instances,
  series = [],
  currentIndex,
  onSelect,
  isLoading = false,
  position = 'left',
  portal,
}: ThumbnailPanelProps) {
  const [collapsedSeries, setCollapsedSeries] = useState<Set<string>>(new Set())
  const containerRef = useRef<HTMLDivElement>(null)
  const activeRef = useRef<HTMLButtonElement>(null)

  // Scroll to active thumbnail when currentIndex changes
  useEffect(() => {
    if (activeRef.current && containerRef.current) {
      activeRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      })
    }
  }, [currentIndex])

  // Group instances by series
  const instancesBySeries = instances.reduce((acc, instance, index) => {
    const seriesUID = instance.seriesInstanceUID
    if (!acc[seriesUID]) {
      acc[seriesUID] = []
    }
    acc[seriesUID].push({ instance, originalIndex: index })
    return acc
  }, {} as Record<string, { instance: ImagenEnPanel; originalIndex: number }[]>)

  const toggleSeries = (seriesUID: string) => {
    setCollapsedSeries((prev) => {
      const next = new Set(prev)
      if (next.has(seriesUID)) {
        next.delete(seriesUID)
      } else {
        next.add(seriesUID)
      }
      return next
    })
  }

  const getSeriesInfo = (seriesUID: string): SerieEnPanel | undefined => {
    return series.find((s) => s.seriesInstanceUID === seriesUID)
  }

  if (isLoading) {
    return (
      <div
        className={cn(
          'w-24 flex-shrink-0 bg-slate-900/95 backdrop-blur border-slate-700/50 flex items-center justify-center',
          position === 'left' ? 'border-r' : 'border-l'
        )}
      >
        <div className="text-center text-slate-400">
          <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />
          <p className="text-xs">Cargando...</p>
        </div>
      </div>
    )
  }

  if (instances.length === 0) {
    return (
      <div
        className={cn(
          'w-24 flex-shrink-0 bg-slate-900/95 backdrop-blur border-slate-700/50 flex items-center justify-center',
          position === 'left' ? 'border-r' : 'border-l'
        )}
      >
        <div className="text-center text-slate-400 px-2">
          <ImageIcon className="h-5 w-5 mx-auto mb-2 opacity-50" />
          <p className="text-xs">Sin imágenes</p>
        </div>
      </div>
    )
  }

  // Single series - simple list without grouping
  if (Object.keys(instancesBySeries).length === 1) {
    return (
      <div
        className={cn(
          'w-24 flex-shrink-0 bg-slate-900/95 backdrop-blur border-slate-700/50 flex flex-col',
          position === 'left' ? 'border-r' : 'border-l'
        )}
      >
        {/* Header */}
        <div className="flex-shrink-0 px-2 py-2 border-b border-slate-700/50">
          <div className="flex items-center gap-1.5 text-slate-400">
            <Layers className="h-3 w-3" />
            <span className="text-xs font-medium">{instances.length} imgs</span>
          </div>
        </div>

        {/* Thumbnails */}
        <div
          ref={containerRef}
          className="flex-1 overflow-y-auto p-1.5 space-y-1"
        >
          {instances.map((instance, index) => (
            <ThumbnailButton
              key={instance.id}
              ref={index === currentIndex ? activeRef : null}
              instance={instance}
              index={index}
              isActive={index === currentIndex}
              onClick={() => onSelect(index)}
              portal={portal}
            />
          ))}
        </div>

        {/* Footer - Current position */}
        <div className="flex-shrink-0 px-2 py-1.5 border-t border-slate-700/50 bg-slate-800/50">
          <p className="text-xs text-center text-slate-400">
            {currentIndex + 1} / {instances.length}
          </p>
        </div>
      </div>
    )
  }

  // Multiple series - grouped view
  return (
    <div
      className={cn(
        'w-28 flex-shrink-0 bg-slate-900/95 backdrop-blur border-slate-700/50 flex flex-col',
        position === 'left' ? 'border-r' : 'border-l'
      )}
    >
      {/* Header */}
      <div className="flex-shrink-0 px-2 py-2 border-b border-slate-700/50">
        <div className="flex items-center gap-1.5 text-slate-400">
          <Layers className="h-3 w-3" />
          <span className="text-xs font-medium">
            {series.length} series • {instances.length} imgs
          </span>
        </div>
      </div>

      {/* Grouped Thumbnails */}
      <div ref={containerRef} className="flex-1 overflow-y-auto">
        {Object.entries(instancesBySeries).map(([seriesUID, items]) => {
          const seriesInfo = getSeriesInfo(seriesUID)
          const isCollapsed = collapsedSeries.has(seriesUID)

          return (
            <div key={seriesUID} className="border-b border-slate-700/30">
              {/* Series Header */}
              <button
                onClick={() => toggleSeries(seriesUID)}
                className="w-full flex items-center justify-between px-2 py-1.5 bg-slate-800/50 hover:bg-slate-700/50 transition-colors"
              >
                <div className="flex-1 min-w-0 text-left">
                  <p className="text-xs font-medium text-slate-300 truncate">
                    {seriesInfo?.seriesDescription || `Serie ${seriesInfo?.seriesNumber || '?'}`}
                  </p>
                  <p className="text-[10px] text-slate-500">{items.length} imgs</p>
                </div>
                {isCollapsed ? (
                  <ChevronDown className="h-3 w-3 text-slate-500 flex-shrink-0" />
                ) : (
                  <ChevronUp className="h-3 w-3 text-slate-500 flex-shrink-0" />
                )}
              </button>

              {/* Series Thumbnails */}
              {!isCollapsed && (
                <div className="p-1.5 space-y-1">
                  {items.map(({ instance, originalIndex }) => (
                    <ThumbnailButton
                      key={instance.id}
                      ref={originalIndex === currentIndex ? activeRef : null}
                      instance={instance}
                      index={originalIndex}
                      isActive={originalIndex === currentIndex}
                      onClick={() => onSelect(originalIndex)}
                      showInstanceNumber
                      portal={portal}
                    />
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Footer - Current position */}
      <div className="flex-shrink-0 px-2 py-1.5 border-t border-slate-700/50 bg-slate-800/50">
        <p className="text-xs text-center text-slate-400">
          {currentIndex + 1} / {instances.length}
        </p>
      </div>
    </div>
  )
}

// =============================================================================
// Thumbnail Button Component
// =============================================================================

interface ThumbnailButtonProps {
  instance: ImagenEnPanel
  index: number
  isActive: boolean
  onClick: () => void
  showInstanceNumber?: boolean
  portal: Portal
}

const ThumbnailButton = ({
  ref,
  instance,
  index,
  isActive,
  onClick,
  showInstanceNumber = false,
  portal,
}: ThumbnailButtonProps & { ref?: React.Ref<HTMLButtonElement> }) => {
  const [imageError, setImageError] = useState(false)

  const thumbnailUrl = useMemo(
    () => (instance.thumbnailUrl ? conCredencial(instance.thumbnailUrl, portal) : null),
    [instance.thumbnailUrl, portal]
  )

  return (
    <button
      ref={ref}
      onClick={onClick}
      className={cn(
        'relative w-full aspect-square rounded-lg overflow-hidden transition-all',
        'bg-slate-800 hover:ring-2 hover:ring-blue-500/50',
        isActive && 'ring-2 ring-blue-500 shadow-lg shadow-blue-500/30'
      )}
    >
      {/* Thumbnail image or placeholder */}
      {thumbnailUrl && !imageError ? (
        <img
          src={thumbnailUrl}
          alt={`Imagen ${index + 1}`}
          className="absolute inset-0 w-full h-full object-cover"
          onError={() => setImageError(true)}
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center bg-slate-800">
          <ImageIcon className="h-4 w-4 text-slate-600" />
        </div>
      )}

      {/* Image number indicator */}
      <div className="absolute bottom-0.5 right-0.5 px-1 py-0.5 bg-black/70 rounded text-[9px] text-white font-mono">
        {showInstanceNumber && instance.instanceNumber != null
          ? instance.instanceNumber
          : index + 1}
      </div>

      {/* Active indicator */}
      {isActive && (
        <div className="absolute top-0.5 left-0.5 w-1.5 h-1.5 rounded-full bg-blue-500" />
      )}
    </button>
  )
}

export default ThumbnailPanel
